import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
  type FilterOperatorObject,
} from '@/components/DataTable';
import {
  PROJECT_FILTER_OPTION_FIELDS,
  type ProjectDataTableItem,
} from '@kirin/api-client/contracts/projects';
import {
  ProjectNameCell,
  RepoUrlCell,
  UsersCountCell,
  AiInstructionsCell,
  HooksCell,
  PolicyCoverageCell,
  SupplyChainCell,
  LastScannedCell,
} from './CellRenderers';
import { PROJECTS_FILTER_URL_KEYS } from './constants';
import {
  fetchProjectFilterOptions,
  getProjectFilterOptionsQueryKey,
} from './api';

const FILTER_OPTIONS_PAGE_SIZE = 10;

// String columns (name, repo URL) behave like the Extensions "name" filter:
// pick from a dynamic dropdown of existing values (INCLUDES / EXCLUDES) or type
// a contains match. The backend maps all of these to ILIKE / IN.
const StringOperators: FilterOperatorObject[] = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
];

/**
 * Column definitions for the Projects DataTable (KIR-3356).
 *
 * Name + Repo URL are filterable via a server-backed dynamic options dropdown
 * (distinct values) — the Extensions-name filter pattern. Other columns are
 * sort-only. Global search also covers name + repo URL.
 */
/**
 * Per-flag column visibility (KIR-3356). Policy coverage rides the AI
 * instruction catalog flag (its data is the catalog coverage rollup); Supply
 * chain rides the packages flag (`agent_command_visibility`, the source of the
 * OSV package data). Both default on so non-flagged callers/tests still get the
 * full set.
 */
export interface ProjectColumnVisibility {
  showPolicyCoverage?: boolean;
  showSupplyChain?: boolean;
}

export function createProjectColumns(
  organizationId: string,
  onSelectProject?: (project: ProjectDataTableItem) => void,
  visibility?: ProjectColumnVisibility
): ColumnConfig<ProjectDataTableItem>[] {
  const showPolicyCoverage = visibility?.showPolicyCoverage ?? true;
  const showSupplyChain = visibility?.showSupplyChain ?? true;

  const columns: ColumnConfig<ProjectDataTableItem>[] = [
    {
      id: 'name',
      header: 'Project name',
      accessor: 'name',
      width: 240,
      renderCell: ({ row }) => (
        <ProjectNameCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'name',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: { urlKey: PROJECTS_FILTER_URL_KEYS.name },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getProjectFilterOptionsQueryKey(
            organizationId,
            PROJECT_FILTER_OPTION_FIELDS.NAME,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchProjectFilterOptions(
            organizationId,
            PROJECT_FILTER_OPTION_FIELDS.NAME,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        pagination: { mode: 'offset', limit: FILTER_OPTIONS_PAGE_SIZE },
      },
    },
    {
      id: 'repoUrl',
      header: 'Repo URL',
      accessor: 'repoOriginUrl',
      width: 280,
      renderCell: ({ row }) => <RepoUrlCell row={row} />,
      sortable: true,
      sortField: 'repoUrl',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: { urlKey: PROJECTS_FILTER_URL_KEYS.repoUrl },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getProjectFilterOptionsQueryKey(
            organizationId,
            PROJECT_FILTER_OPTION_FIELDS.REPO_URL,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchProjectFilterOptions(
            organizationId,
            PROJECT_FILTER_OPTION_FIELDS.REPO_URL,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        pagination: { mode: 'offset', limit: FILTER_OPTIONS_PAGE_SIZE },
      },
    },
    {
      id: 'users',
      header: 'Users',
      accessor: 'userCount',
      width: 100,
      renderCell: ({ row }) => (
        <UsersCountCell row={row} onSelectProject={onSelectProject} />
      ),
      sortable: true,
      sortField: 'users',
    },
    {
      id: 'aiInstructions',
      header: 'AI Instructions',
      accessor: 'aiInstructionsCount',
      width: 180,
      renderCell: ({ row }) => (
        <AiInstructionsCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'aiInstructions',
    },
    {
      id: 'hooks',
      header: 'Hooks',
      accessor: 'hooksCount',
      width: 120,
      renderCell: ({ row }) => (
        <HooksCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'hooks',
    },
  ];

  if (showPolicyCoverage) {
    columns.push({
      id: 'policyCoverage',
      header: 'Policy coverage',
      accessor: 'coverage',
      width: 220,
      renderCell: ({ row }) => <PolicyCoverageCell row={row} />,
      sortable: true,
      // Server sorts by gaps (missing + partial); this is the tab's default.
      sortField: 'coverageGaps',
    });
  }

  if (showSupplyChain) {
    columns.push({
      id: 'supplyChain',
      header: 'Packages added by AI',
      description:
        'Shows the list of packages added to the project by coding agents within the last 7 days',
      accessor: 'supplyChain',
      width: 210,
      renderCell: ({ row }) => <SupplyChainCell row={row} />,
      sortable: true,
      sortField: 'supplyChain',
    });
  }

  // "Last scanned" is the coverage-scan time — meaningful only when the catalog
  // (coverage) is enabled, so it rides the same flag as Policy coverage.
  if (showPolicyCoverage) {
    columns.push({
      id: 'lastScanned',
      header: 'Last scanned',
      timestamp: true,
      accessor: 'lastScannedAt',
      renderCell: ({ row }) => <LastScannedCell row={row} />,
      sortable: true,
      sortField: 'lastScannedAt',
    });
  }

  return columns;
}
